/**
 * Connector wizard registry — one config per connector.
 *
 * Adding a connector = appending an entry. Editing fields/validation = changing
 * an entry. No code changes required outside this file as long as the existing
 * step kinds (credentials/test/select/preview/finish) cover the flow.
 */

import { connectors } from "../../../data/connectors";
import { resolveConnectorIconUrl } from "../../../utils/connectorIcons";
import type {
  BackendConnectorCatalogSchema,
  BackendConnectorWizardStep,
} from "../../../services/api";
import type {
  ConnectorFieldType,
  ConnectorStepConfig,
  ConnectorWizardConfig,
} from "./ConnectorWizardModal.types";

const findConnectorMeta = (catalogKey: string) => {
  // Catalog keys come from the registry mock — case-insensitive lookup against
  // the static connector data file (which keys off `name`).
  const target = catalogKey.toLowerCase().replace(/\s+/g, "");
  return connectors.find(
    (c) => c.name.toLowerCase().replace(/\s+/g, "") === target,
  );
};

const SNOWFLAKE_META = findConnectorMeta("snowflake");
const TABLEAU_META = findConnectorMeta("tableau");
const POWERBI_META = findConnectorMeta("powerbi");
const GOOGLE_SHEETS_META = findConnectorMeta("googlesheets");
const HUBSPOT_META = findConnectorMeta("hubspot");

/**
 * Snowflake config — fields and validation match the Credentials Figma
 * (node 1191:219021). The seventh field labelled "Password *" in the design
 * is treated as `role` here; the Figma label is a designer typo flagged for
 * review and trivial to relabel without touching code.
 */
const snowflakeConfig: ConnectorWizardConfig = {
  catalogKey: "snowflake",
  displayName: SNOWFLAKE_META?.header ?? "Snowflake",
  subtitle: "Connect to your Snowflake data warehouse",
  iconUrl: SNOWFLAKE_META?.logo ?? "/logos/snowflake.svg",
  steps: [
    {
      id: "credentials",
      kind: "credentials",
      label: "Credentials",
      title: "Credentials",
      primaryCtaLabel: "Test connection",
      fields: [
        {
          key: "connection_name",
          label: "Connection name",
          type: "text",
          width: "half",
          required: true,
          placeholder: "Snowflake production",
          validators: [{ kind: "maxLength", value: 64 }],
        },
        {
          key: "account",
          label: "Account",
          type: "text",
          width: "half",
          required: true,
          placeholder: "xy12345.ap-south-1.aws",
        },
        {
          key: "warehouse",
          label: "Warehouse",
          type: "text",
          width: "half",
          required: true,
          placeholder: "COMPUTE_WH",
        },
        {
          key: "database",
          label: "Database",
          type: "text",
          width: "half",
          required: true,
          placeholder: "ANALYTICS_DB",
        },
        {
          key: "username",
          label: "Username",
          type: "text",
          width: "half",
          required: true,
          autoComplete: "username",
        },
        {
          key: "password",
          label: "Password",
          type: "password",
          width: "half",
          required: true,
          autoComplete: "current-password",
        },
        {
          key: "role",
          label: "Role",
          type: "text",
          width: "half",
          required: true,
          placeholder: "ACCOUNTADMIN",
          helperText: "Snowflake role used for the connection",
        },
      ],
    },
    {
      id: "test",
      kind: "test",
      label: "Test",
      title: "Test connection",
      primaryCtaLabel: "Continue",
    },
    {
      id: "select",
      kind: "select",
      label: "Select",
      title: "Select tables",
      primaryCtaLabel: "Continue",
      skipCtaLabel: "Skip",
      allowSkip: true,
    },
    {
      id: "preview",
      kind: "preview",
      label: "Preview",
      title: "Selected tables",
      primaryCtaLabel: "Import",
      submitOnClick: true,
    },
    { id: "finish", kind: "finish", label: "Finish", title: "All set" },
  ],
  behavior: {
    requiresAssetSelection: true,
    assetSyncOnSelectEnter: true,
    assetSyncMaxDepth: 2,
    assetSyncPageLimit: 500,
    previewPageSizeOptions: [5, 10],
    uiVariant: "default_relational",
  },
};

/**
 * Tableau config — BI tool, 3-step flow (no schema/table picker since the
 * entire data catalog is imported on connect). Fields and the bottom note
 * match the Tableau credentials Figma.
 */
const tableauConfig: ConnectorWizardConfig = {
  catalogKey: "tableau",
  displayName: TABLEAU_META?.header ?? "Tableau",
  subtitle: "Connect to your Tableau workspace",
  iconUrl: TABLEAU_META?.logo ?? "/logos/tableau.svg",
  steps: [
    {
      id: "credentials",
      kind: "credentials",
      label: "Credentials",
      title: "Credentials",
      note: "Entire data catalog will be imported",
      primaryCtaLabel: "Test connection",
      fields: [
        {
          key: "connection_name",
          label: "Connection name",
          type: "text",
          width: "half",
          required: true,
          placeholder: "Tableau production",
          validators: [{ kind: "maxLength", value: 64 }],
        },
        {
          key: "server_url",
          label: "Server URL",
          type: "url",
          width: "half",
          required: true,
          placeholder: "https://tableau.yourcompany.com",
          validators: [{ kind: "url" }],
        },
        {
          key: "site_name",
          label: "Site name",
          type: "text",
          width: "half",
          required: true,
        },
        {
          key: "pat_name",
          label: "Personal access token name",
          type: "text",
          width: "half",
          required: true,
          autoComplete: "off",
        },
        {
          key: "pat_secret",
          label: "Personal access token secret key",
          type: "password",
          required: true,
          autoComplete: "off",
        },
      ],
    },
    {
      id: "test",
      kind: "test",
      label: "Test",
      title: "Test connection",
      primaryCtaLabel: "Continue",
    },
    {
      id: "select",
      kind: "select",
      label: "Select",
      title: "Select catalog",
      primaryCtaLabel: "Import selected",
      skipCtaLabel: "Import all",
      allowSkip: true,
    },
    {
      id: "preview",
      kind: "preview",
      label: "Preview",
      title: "Selected catalog",
      primaryCtaLabel: "Finish",
      submitOnClick: true,
    },
    { id: "finish", kind: "finish", label: "Finish", title: "All set" },
  ],
  behavior: {
    requiresAssetSelection: true,
    assetSyncOnSelectEnter: false,
    previewPageSizeOptions: [5, 10],
    uiVariant: "bi_relational_tree",
    treeHints: {
      select_info_message:
        "You can choose specific dashboards and reports, or skip this step to import everything.",
      preview_all_selected_message: "All catalog items selected.",
    },
  },
};

const powerBiConfig: ConnectorWizardConfig = {
  catalogKey: "powerbi",
  displayName: POWERBI_META?.header ?? "Power BI",
  subtitle: "Connect to your Power BI workspace",
  iconUrl: POWERBI_META?.logo ?? "/logos/powerbi.svg",
  steps: [
    {
      id: "credentials",
      kind: "credentials",
      label: "Credentials",
      title: "Credentials",
      primaryCtaLabel: "Test connection",
      fields: [
        {
          key: "connection_name",
          label: "Connection name",
          type: "text",
          width: "half",
          required: true,
          placeholder: "Power BI production",
          validators: [{ kind: "maxLength", value: 64 }],
        },
        {
          key: "tenant_id",
          label: "Tenant ID",
          type: "text",
          width: "half",
          required: true,
        },
        {
          key: "client_id",
          label: "Client ID",
          type: "text",
          width: "half",
          required: true,
        },
        {
          key: "client_secret",
          label: "Client secret",
          type: "password",
          width: "half",
          required: true,
          autoComplete: "off",
        },
      ],
    },
    {
      id: "test",
      kind: "test",
      label: "Test",
      title: "Test connection",
      primaryCtaLabel: "Continue",
    },
    {
      id: "select",
      kind: "select",
      label: "Select",
      title: "Select catalog",
      primaryCtaLabel: "Import selected",
      skipCtaLabel: "Import all",
      allowSkip: true,
    },
    {
      id: "preview",
      kind: "preview",
      label: "Preview",
      title: "Selected catalog",
      primaryCtaLabel: "Finish",
      submitOnClick: true,
    },
    { id: "finish", kind: "finish", label: "Finish", title: "All set" },
  ],
  behavior: {
    requiresAssetSelection: true,
    assetSyncOnSelectEnter: false,
    previewPageSizeOptions: [5, 10],
    uiVariant: "bi_relational_tree",
    treeHints: {
      select_info_message:
        "You can choose specific dashboards and reports, or skip this step to import everything.",
      preview_all_selected_message: "All catalog items selected.",
    },
  },
};

/**
 * HubSpot config — CRM SaaS, Snowflake-style selection flow (object/table
 * picker, not the BI dashboard tree Tableau/Power BI use).
 *
 * Single credential field: HubSpot auth is a static Private App token (an
 * admin generates it in HubSpot's own UI and pastes it here) — not an OAuth
 * redirect, the same static-credential shape as Tableau's PAT/Power BI's
 * service principal. `private_app_token` is the exact field name
 * `clients/hubspot.py`'s `_access_token` reads (preferred over its
 * `access_token`/`token` fallbacks), so this key must stay in sync with that
 * backend contract.
 */
const hubspotConfig: ConnectorWizardConfig = {
  catalogKey: "hubspot",
  displayName: HUBSPOT_META?.header ?? "HubSpot",
  subtitle: "Connect to your HubSpot portal",
  iconUrl: HUBSPOT_META?.logo ?? "/logos/hubspot.svg",
  steps: [
    {
      id: "credentials",
      kind: "credentials",
      label: "Credentials",
      title: "Credentials",
      note: "Entire data catalog will be imported",
      primaryCtaLabel: "Test connection",
      fields: [
        {
          key: "connection_name",
          label: "Connection name",
          type: "text",
          width: "half",
          required: true,
          placeholder: "HubSpot production",
          validators: [{ kind: "maxLength", value: 64 }],
        },
        {
          key: "private_app_token",
          label: "Private app token",
          type: "password",
          required: true,
          autoComplete: "off",
          helperText: "Generate this in HubSpot under Settings → Integrations → Private Apps",
        },
      ],
    },
    {
      id: "test",
      kind: "test",
      label: "Test",
      title: "Test connection",
      primaryCtaLabel: "Continue",
    },
    {
      id: "select",
      kind: "select",
      label: "Select",
      title: "Select objects",
      primaryCtaLabel: "Continue",
      skipCtaLabel: "Skip",
      allowSkip: true,
    },
    {
      id: "preview",
      kind: "preview",
      title: "Selected objects",
      label: "Preview",
      primaryCtaLabel: "Import",
      submitOnClick: true,
    },
    { id: "finish", kind: "finish", label: "Finish", title: "All set" },
  ],
  behavior: {
    requiresAssetSelection: true,
    assetSyncOnSelectEnter: true,
    assetSyncMaxDepth: 2,
    assetSyncPageLimit: 500,
    previewPageSizeOptions: [5, 10],
    uiVariant: "default_relational",
  },
};

/**
 * Google Sheets config — a single OAuth entry point ("Continue with Google")
 * feeding the same downstream Test -> Select -> Preview -> Finish flow, with
 * two outcomes decided purely by whether the paired "Link to Google sheet"
 * field has a value when the connection is saved:
 *
 * 1. Empty URL, button clicked directly — OAuth authorizes the whole
 *    account; the Select step lists every spreadsheet the user can see
 *    (Drive-based listing, `drive.metadata.readonly` scope).
 * 2. URL pasted first — the same OAuth popup fires automatically off the
 *    URL field (see `CredentialsStep.tsx`'s blur-triggered `startOAuth`
 *    call); once consent completes, the Select step scopes to just that one
 *    file (`GoogleSheetsClient.fetch_metadata` branches purely on
 *    `config_json.spreadsheet_url` being present).
 *
 * The service-account-key manual fallback was removed 2026-08-26 — OAuth is
 * now the only auth mechanism.
 */
const googleSheetsConfig: ConnectorWizardConfig = {
  catalogKey: "google_sheets",
  displayName: GOOGLE_SHEETS_META?.header ?? "Google Sheets",
  subtitle: "Connect a Google Sheet to your knowledge graph",
  iconUrl: GOOGLE_SHEETS_META?.logo ?? "/logos/google_sheets.svg",
  steps: [
    {
      id: "credentials",
      kind: "credentials",
      label: "Credentials",
      title: "Credentials",
      primaryCtaLabel: "Test connection",
      oauthProvider: "google_sheets",
      fields: [
        {
          key: "connection_name",
          label: "Connection name",
          type: "text",
          width: "half",
          required: true,
          placeholder: "Google Sheets production",
          validators: [{ kind: "maxLength", value: 64 }],
        },
        {
          key: "connection_mode",
          label: "Connection mode",
          type: "select",
          width: "half",
          required: true,
          defaultValue: "import_once",
          options: [
            { value: "import_once", label: "Import once" },
            { value: "live_sync", label: "Live sync" },
          ],
          helperText: "Choose how data from this sheet should be synced.",
        },
        {
          key: "spreadsheet_url",
          label: "Google Sheet URL",
          type: "url",
          width: "full",
          // Optional: leaving this empty and clicking "Continue with
          // Google" browses the whole account (Drive listing). Pasting a
          // URL here — with or without clicking the button first —
          // auto-triggers the same OAuth popup (see CredentialsStep.tsx)
          // and scopes the connection to just this one file. The field
          // stays visible and keeps its value after OAuth succeeds; it is
          // never hidden or cleared.
          required: false,
          placeholder:
            "https://docs.google.com/spreadsheets/d/1AbCDeFGhijkLmNoPQRstuv/edit",
          helperText:
            "Paste the URL of the Google Sheet to connect. The sheet ID is parsed out of this URL.",
        },
      ],
    },
    {
      id: "test",
      kind: "test",
      label: "Test",
      title: "Test connection",
      primaryCtaLabel: "Continue",
    },
    {
      id: "select",
      kind: "select",
      label: "Select",
      title: "Select sheets",
      primaryCtaLabel: "Continue",
      skipCtaLabel: "Skip",
      allowSkip: true,
    },
    {
      id: "preview",
      kind: "preview",
      label: "Preview",
      title: "Selected sheets",
      primaryCtaLabel: "Import",
      submitOnClick: true,
    },
    { id: "finish", kind: "finish", label: "Finish", title: "All set" },
  ],
  behavior: {
    requiresAssetSelection: true,
    assetSelectionRequired: true,
    assetSyncOnSelectEnter: true,
    assetSyncMaxDepth: 2,
    previewPageSizeOptions: [5, 10],
    uiVariant: "default_relational",
  },
};

const REGISTRY: Record<string, ConnectorWizardConfig> = {
  snowflake: snowflakeConfig,
  tableau: tableauConfig,
  powerbi: powerBiConfig,
  google_sheets: googleSheetsConfig,
  hubspot: hubspotConfig,
  // Add other connectors here. Until they're configured the wizard falls
  // back to a "Coming soon" message so card clicks still feel responsive.
};

export const getConnectorWizardConfig = (
  catalogKey: string,
): ConnectorWizardConfig | null => REGISTRY[catalogKey] ?? null;

export const hasConnectorWizardConfig = (catalogKey: string): boolean =>
  catalogKey in REGISTRY;

interface JsonSchemaProperty {
  title?: string;
  description?: string;
  format?: string;
  type?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  default?: unknown;
  enum?: unknown[];
  "x-magicbi-widget"?: unknown;
  "x-magicbi-rows"?: unknown;
  "x-magicbi-width"?: unknown;
  "x-magicbi-file"?: {
    accept?: unknown;
    maxBytes?: unknown;
  };
}

interface JsonSchemaObject {
  required?: string[];
  properties?: Record<string, JsonSchemaProperty>;
}

const toLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toStepTitle = (label: string): string => {
  const normalized = label.trim().toLowerCase();
  if (normalized === "test") return "Test connection";
  if (normalized === "select") return "Select tables";
  if (normalized === "preview") return "Selected tables";
  if (normalized === "finish") return "All set";
  return label;
};

const toConnectionModeLabel = (mode: string): string => {
  if (mode === "import_once") return "Import once";
  if (mode === "live_sync") return "Live sync";
  return toLabel(mode);
};

const CONNECTION_MODE_ORDER = ["import_once", "live_sync"] as const;

/**
 * Backend catalog keys whose credentials step mints a refresh_token via an
 * in-app OAuth redirect (see `oauthProvider` below) rather than a manual
 * token-paste form. Salesforce was the only entry until Google Sheets;
 * extend this set, don't special-case individual keys inline.
 */
const OAUTH_SCHEMA_CATALOG_KEYS = new Set(["salesforce", "google_sheets"]);

const getDefaultPrimaryCtaLabel = (
  kind: BackendConnectorWizardStep["value"],
): string | undefined => {
  switch (kind) {
    case "credentials":
      return "Test connection";
    case "test":
      return "Continue";
    case "select":
      return "Continue";
    case "preview":
      return "Import";
    case "finish":
      return "Done";
    default:
      return undefined;
  }
};

const isKnownWidget = (value: unknown): value is ConnectorFieldType =>
  value === "text" ||
  value === "email" ||
  value === "password" ||
  value === "url" ||
  value === "number" ||
  value === "select" ||
  value === "textarea" ||
  value === "file";

const isKnownWidth = (value: unknown): value is "half" | "full" =>
  value === "half" || value === "full";

export const resolveFieldType = (
  key: string,
  property: JsonSchemaProperty,
): ConnectorFieldType => {
  const normalizedKey = key.toLowerCase();
  if (isKnownWidget(property["x-magicbi-widget"])) {
    return property["x-magicbi-widget"];
  }
  // Fallback safety: even if backend catalog hasn't rolled out x-magicbi-widget
  // yet, BigQuery service account keys should still use file upload UX.
  if (normalizedKey === "service_account_json") return "file";
  if (Array.isArray(property.enum) && property.enum.length > 0) return "select";
  if (property.type === "integer" || property.type === "number")
    return "number";
  if (property.format === "textarea") return "textarea";
  if (property.format === "uri") return "url";
  if (property.format === "email") return "email";
  if (property.format === "password") return "password";
  if (normalizedKey.includes("secret") || normalizedKey.includes("password")) {
    return "password";
  }
  return "text";
};

const resolveFieldWidth = (
  type: ConnectorFieldType,
  property: JsonSchemaProperty,
): "half" | "full" => {
  if (isKnownWidth(property["x-magicbi-width"])) {
    return property["x-magicbi-width"];
  }
  if (type === "textarea") return "full";
  return "half";
};

const schemaToFields = (schema: JsonSchemaObject | null | undefined) => {
  if (!schema || typeof schema !== "object") return [];
  const properties = schema.properties;
  if (!properties || typeof properties !== "object") return [];

  const requiredFields = new Set(schema.required ?? []);

  return Object.entries(properties).map(([key, property]) => {
    const validators: Array<
      | { kind: "minLength"; value: number }
      | { kind: "maxLength"; value: number }
      | { kind: "pattern"; value: RegExp }
      | { kind: "url" }
    > = [];

    if (typeof property.minLength === "number") {
      validators.push({ kind: "minLength", value: property.minLength });
    }
    if (typeof property.maxLength === "number") {
      validators.push({ kind: "maxLength", value: property.maxLength });
    }
    if (typeof property.pattern === "string") {
      try {
        validators.push({
          kind: "pattern",
          value: new RegExp(property.pattern),
        });
      } catch {
        // Ignore invalid backend regex so the wizard still renders.
      }
    }
    if (property.format === "uri") {
      validators.push({ kind: "url" });
    }

    const type = resolveFieldType(key, property);
    const xMagicbiFile =
      property["x-magicbi-file"] &&
      typeof property["x-magicbi-file"] === "object"
        ? property["x-magicbi-file"]
        : undefined;
    const fileAccept = Array.isArray(xMagicbiFile?.accept)
      ? xMagicbiFile.accept.filter(
          (item): item is string =>
            typeof item === "string" && item.trim().length > 0,
        )
      : undefined;
    let fileMaxBytes =
      typeof xMagicbiFile?.maxBytes === "number" && xMagicbiFile.maxBytes > 0
        ? xMagicbiFile.maxBytes
        : undefined;
    let resolvedFileAccept = fileAccept;
    if (type === "file" && key.toLowerCase() === "service_account_json") {
      if (!resolvedFileAccept || resolvedFileAccept.length === 0) {
        resolvedFileAccept = [".json", "application/json"];
      }
      if (fileMaxBytes === undefined) {
        fileMaxBytes = 1048576;
      }
    }
    const rows =
      typeof property["x-magicbi-rows"] === "number" &&
      Number.isInteger(property["x-magicbi-rows"]) &&
      property["x-magicbi-rows"] > 0
        ? property["x-magicbi-rows"]
        : undefined;

    return {
      key,
      label: property.title ?? toLabel(key),
      type,
      required: requiredFields.has(key),
      helperText: property.description,
      defaultValue:
        typeof property.default === "string" ? property.default : undefined,
      width: resolveFieldWidth(type, property),
      rows,
      fileAccept: resolvedFileAccept,
      fileMaxBytes,
      options: Array.isArray(property.enum)
        ? property.enum
            .filter((value): value is string => typeof value === "string")
            .map((value) => ({ value, label: toLabel(value) }))
        : undefined,
      validators,
    };
  });
};

const DEFAULT_BACKEND_STEPS: BackendConnectorWizardStep[] = [
  { value: "credentials", label: "Credentials" },
  { value: "test", label: "Test" },
  { value: "finish", label: "Finish", submit_on_click: true },
];

const normalizeSubmitSteps = (steps: ConnectorStepConfig[]) => {
  const candidates = steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => step.kind !== "finish");
  const submitIndexes = candidates
    .filter(({ step }) => Boolean(step.submitOnClick))
    .map(({ index }) => index);

  // Valid configuration: exactly one non-finish submit step.
  if (submitIndexes.length === 1) return steps;

  const normalized = steps.map((step) => ({
    ...step,
    submitOnClick: step.kind === "finish" ? false : step.submitOnClick,
  }));

  // Fallback strategy: if backend marks none/many submit points, force the
  // last non-finish step to be the submit point so the flow still works.
  const fallbackCandidate = [...candidates].pop();
  if (fallbackCandidate) {
    normalized[fallbackCandidate.index].submitOnClick = true;
  }

  return normalized;
};

export const buildConnectorWizardConfigFromSchema = (
  schema: BackendConnectorCatalogSchema,
): ConnectorWizardConfig => {
  const meta = findConnectorMeta(schema.key);
  const authFields = schemaToFields(schema.auth_schema as JsonSchemaObject);
  const configFields = schemaToFields(schema.config_schema as JsonSchemaObject);
  const backendModes = new Set(schema.supported_connection_modes ?? []);
  const normalizedModes = CONNECTION_MODE_ORDER.filter(
    (mode) => backendModes.has(mode) || CONNECTION_MODE_ORDER.includes(mode),
  );
  const modeOptions = normalizedModes.map((mode) => ({
    value: mode,
    label: toConnectionModeLabel(mode),
  }));
  const defaultMode = "live_sync";
  const connectionModeField = {
    key: "connection_mode",
    label: "Connection mode",
    type: "select" as const,
    width: "half" as const,
    required: true,
    defaultValue: defaultMode,
    options: modeOptions,
    helperText: "Choose how data from this source should be synced.",
    helperTextByValue: {
      import_once:
        "Import data one time. Changes in the source won't be reflected automatically.",
      live_sync:
        "Keep data updated automatically whenever it changes in the source.",
    },
  };
  const mergedFields = [
    {
      key: "connection_name",
      label: "Connection name",
      type: "text" as const,
      width: "half" as const,
      required: true,
      placeholder: `${schema.display_name} production`,
      validators: [{ kind: "maxLength" as const, value: 64 }],
    },
    ...configFields,
    ...authFields,
    connectionModeField,
  ];

  const backendSteps =
    schema.wizard_steps && schema.wizard_steps.length > 0
      ? schema.wizard_steps
      : DEFAULT_BACKEND_STEPS;

  const mappedSteps = backendSteps.map((step) => {
    const label = step.label?.trim() || toLabel(step.value);
    return {
      id: step.value,
      kind: step.value,
      label,
      title: step.title?.trim() || toStepTitle(label),
      note: step.value === "credentials" ? step.note : undefined,
      columns: step.value === "credentials" ? 2 : undefined,
      fields: step.value === "credentials" ? mergedFields : undefined,
      // Salesforce mints its refresh_token via an in-app OAuth redirect, so its
      // credentials step shows a "Connect with Salesforce" button rather than a
      // manual token-paste form (the paste fields stay as an advanced fallback).
      oauthProvider:
        step.value === "credentials" && OAUTH_SCHEMA_CATALOG_KEYS.has(schema.key)
          ? schema.key
          : undefined,
      oauthVisibleFieldKeys:
        step.value === "credentials" && schema.key === "google_sheets"
          ? ["connection_mode"]
          : undefined,
      oauthPairedFieldKey:
        step.value === "credentials" && schema.key === "google_sheets"
          ? "spreadsheet_url"
          : undefined,
      primaryCtaLabel:
        step.primary_cta_label ?? getDefaultPrimaryCtaLabel(step.value),
      skipCtaLabel: step.skip_cta_label,
      submitOnClick: step.submit_on_click,
      allowSkip: step.allow_skip,
    };
  });
  const steps = normalizeSubmitSteps(mappedSteps);

  return {
    catalogKey: schema.key,
    displayName: schema.display_name,
    subtitle: `Connect to your ${schema.display_name} source`,
    iconUrl: resolveConnectorIconUrl({
      catalogKey: schema.key,
      categoryType: meta?.type,
      localLogo: meta?.logo,
    }),
    steps,
    behavior: {
      requiresAssetSelection: schema.wizard_behavior?.requires_asset_selection,
      assetSyncOnSelectEnter:
        schema.wizard_behavior?.asset_sync_on_select_enter,
      assetSyncMaxDepth: schema.wizard_behavior?.asset_sync_max_depth,
      assetSyncPageLimit: schema.wizard_behavior?.asset_sync_page_limit,
      previewPageSizeOptions: schema.wizard_behavior?.preview_page_size_options,
      uiVariant: schema.wizard_behavior?.ui_variant,
      treeHints: schema.wizard_behavior?.tree_hints,
    },
  };
};
